import React, { useState, useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { startLocalSession } from '../../../../../../../../crud/management_api/local_sessions';
import { getLocalCajaManagement, openLocalCaja } from '../../../../../../../../crud/management_api/locales_caja_management';
import { toast } from 'react-toastify';
import { FaXmark, FaTriangleExclamation, FaCheck, FaSpinner, FaPlay, FaClock, FaDollarSign, FaCashRegister, FaCircleCheck } from 'react-icons/fa6';
import { formatCurrency } from '../../../../../../../../utils/functions';
import './StartSession.scss';


interface ModalProps {
  show: boolean;                  // Controla visibilidad del modal
  handleClose: () => void;        // Función para cerrar el modal
  onRedirectToArqueos ?: () => void; // Función para redirigir a arqueos
}

interface Caja {
  workstation_id: number;
  workstation_name: string;
  last_modified: string;
  last_opened: string;
  last_closed: string;
  user_id: number;
  user_name: string;
  is_open: boolean;
  initial_amount: number;
  current_amount: number;
  final_amount: number;
}

type ProcessStep = 'setup' | 'starting-session' | 'opening-caja' | 'completed';



const StartSession: React.FC<ModalProps> = ({
  show,
  handleClose
}) => {
  const [currentStep, setCurrentStep] = useState<ProcessStep>('setup');
  const [cajas, setCajas] = useState<Caja[]>([]);
  const [selectedCaja, setSelectedCaja] = useState<Caja | null>(null);
  const [initialAmount, setInitialAmount] = useState<number | null>(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [cajaOpened, setCajaOpened] = useState(false);

  // Load cajas when modal opens
  useEffect(() => {
    if (show) {
      const user = JSON.parse(localStorage.getItem('user') ?? '{}');
      const localId = user ? Number(user.local_id) : 0;
      const userId = user ? Number(user.user_id) : 0;

      getLocalCajaManagement(localId, userId)
        .then((response) => {
          const closedCajas = response.data.filter((caja: Caja) => !caja.is_open);
          setCajas(closedCajas);
          
          // Auto-select if only one closed caja
          if (closedCajas.length === 1) {
            setSelectedCaja(closedCajas[0]);
            setInitialAmount(closedCajas[0].final_amount ?? 0);
          }
        })
        .catch((error) => {
          console.error('Error getting cajas: ', error);
          toast.error('Error obteniendo cajas');
        });
    }
  }, [show]);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setCurrentStep('setup');
      setSelectedCaja(null);
      setInitialAmount(null);
      setSessionStarted(false);
      setCajaOpened(false);
      setCajas([]);
    }
  }, [show]);

  const handleStartFlow = async () => {
    if (!selectedCaja || initialAmount === null) {
      toast.error('Debe seleccionar una caja e ingresar un monto inicial');
      return;
    }

    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const localId = user ? Number(user.local_id) : 0;
    const userId = user ? Number(user.user_id) : 0;

    try {
      // Step 1: Start Session
      setCurrentStep('starting-session');
      await startLocalSession(localId, userId);
      setSessionStarted(true);
      
      // Small delay for UX
      await new Promise(resolve => setTimeout(resolve, 800));

      // Step 2: Open Caja
      setCurrentStep('opening-caja');
      await openLocalCaja(userId, selectedCaja.workstation_id, initialAmount);
      setCajaOpened(true);
      
      // Step 3: Complete
      setCurrentStep('completed');
      toast.success('Sesión iniciada y caja abierta correctamente');
      
      // Close and reload after delay
      setTimeout(() => {
        handleClose();
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error('Error in flow: ', error);
      toast.error('Error en el proceso');
      setCurrentStep('setup');
      setSessionStarted(false);
      setCajaOpened(false);
    }
  };

  // Calculate amount difference for warnings
  const expectedAmount = selectedCaja?.final_amount ?? 0;
  const declaredAmount = initialAmount ?? 0;
  const difference = declaredAmount - expectedAmount;

  const isProcessing = ['starting-session', 'opening-caja', 'completed'].includes(currentStep);
  const canProceed = selectedCaja && initialAmount !== null && !isProcessing;

  

  

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      className="start-session-modal"
      centered
    >
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">Iniciar Sesión</Modal.Title>
        </div>
        <Button variant="none" onClick={handleClose}>
          <FaXmark size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body>
        {currentStep === 'setup' && (
          <div className="session-setup">
            {/* Caja Selection */}
            <div className="form-section">
              <div className="field-label">Seleccionar Caja</div>
              <div className="caja-options">
                {cajas.map((caja) => (
                  <div
                    key={caja.workstation_id}
                    className={`caja-option ${selectedCaja?.workstation_id === caja.workstation_id ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedCaja(caja);
                      setInitialAmount(caja.final_amount ?? 0);
                    }}
                  >
                    <div className="caja-info">
                      <div className="caja-name">{caja.workstation_name}</div>
                      <div className="caja-status">Cerrada</div>
                    </div>
                    <div className="caja-amount">{formatCurrency(caja.final_amount ?? 0)}</div>
                  </div>
                ))}
              </div>
              {cajas.length === 0 && (
                <div className="empty-state">
                  <FaTriangleExclamation />
                  <span>No hay cajas cerradas disponibles</span>
                </div>
              )}
            </div>

            {/* Amount Input */}
            {selectedCaja && (
              <div className="form-section">
                <div className="field-label">Monto Inicial</div>
                <div className="amount-input-group">
                  <div className="currency-symbol">$</div>
                  <input
                    type="text"
                    className="amount-input"
                    value={
                      initialAmount != null
                        ? initialAmount.toLocaleString('es-CR', {
                            maximumFractionDigits: 0,
                          })
                        : ''
                    }
                    onChange={(e) => {
                      const onlyNums = e.target.value.replace(/\D/g, '');
                      setInitialAmount(onlyNums ? Number(onlyNums) : null);
                    }}
                    placeholder="0"
                  />
                </div>
                
                {/* Amount Warning */}
                {initialAmount !== null && Math.abs(difference) > 0.01 && (
                  <div className={`warning-banner ${difference > 0 ? 'deposit-warning' : 'withdrawal-warning'}`}>
                    <FaTriangleExclamation className="warning-icon" />
                    <div className="warning-content">
                      <div className="warning-title">
                        {difference > 0 ? 'Abono de dinero' : 'Retiro de dinero'}
                      </div>
                      <div className="warning-message">
                        Un {difference > 0 ? 'abono' : 'retiro'} de{' '}
                        <span className="amount-highlight">{formatCurrency(Math.abs(difference))}</span>{' '}
                        será registrado
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Processing Steps */}
        {currentStep !== 'setup' && (
          <div className="processing-container">
            <div className="process-steps">
              <div className={`process-step ${sessionStarted ? 'completed' : currentStep === 'starting-session' ? 'active' : ''}`}>
                <div className="step-icon">
                  {sessionStarted ? <FaCheck /> : <FaSpinner className="spinning" />}
                </div>
                <div className="step-content">
                  <div className="step-title">Iniciando sesión</div>
                  <div className="step-description">Configurando el sistema...</div>
                </div>
              </div>

              <div className={`process-step ${cajaOpened ? 'completed' : currentStep === 'opening-caja' ? 'active' : ''}`}>
                <div className="step-icon">
                  {cajaOpened ? <FaCheck /> : currentStep === 'opening-caja' ? <FaSpinner className="spinning" /> : <FaClock />}
                </div>
                <div className="step-content">
                  <div className="step-title">Abriendo {selectedCaja?.workstation_name}</div>
                  <div className="step-description">Registrando monto inicial...</div>
                </div>
              </div>

              <div className={`process-step ${currentStep === 'completed' ? 'completed' : ''}`}>
                <div className="step-icon">
                  {currentStep === 'completed' ? <FaCheck /> : <FaClock />}
                </div>
                <div className="step-content">
                  <div className="step-title">Proceso completado</div>
                  <div className="step-description">¡Listo para trabajar!</div>
                </div>
              </div>
            </div>

            {currentStep === 'completed' && (
              <div className="success-message">
                <FaCircleCheck className="success-icon" />
                <div className="success-text">
                  <div className="success-title">¡Sesión iniciada correctamente!</div>
                  <div className="success-subtitle">Recargando página...</div>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          {currentStep === 'setup' && (
            <>
              <Button
                variant="none"
                className="form-button-cancel"
                onClick={handleClose}
                disabled={isProcessing}
              >
                Cancelar
              </Button>
              <Button
                variant="none"
                className="form-button-create"
                onClick={handleStartFlow}
                disabled={!canProceed}
              >
                <FaPlay /> Iniciar Sesión y Abrir Caja
              </Button>
            </>
          )}
          
          {isProcessing && currentStep !== 'completed' && (
            <Button variant="none" className="form-button-create" disabled>
              <FaSpinner className="spinning" /> Procesando...
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default StartSession;
